import React, { useEffect, useRef } from "react"
import { ORDERS, setOrder } from "../utils/iconOrder"

/* Keeps the menu inside the desktop when the click lands near an edge.
   Measured after mount rather than guessed at, because the width depends on
   the longest label.

   The bottom bound is the taskbar's top edge, not the viewport's: the taskbar
   is opaque and stacks above this, so a menu merely kept on screen still gets
   its last item swallowed. Flipping it up is what a real one does anyway. */
const clampToDesktop = element => {
  const rect = element.getBoundingClientRect()
  const margin = 8
  const taskbar = document.querySelector(".taskbar")
  const floor = taskbar ? taskbar.getBoundingClientRect().top : window.innerHeight

  const overflowX = rect.right - (window.innerWidth - margin)
  const overflowY = rect.bottom - (floor - margin)

  if (overflowX > 0) element.style.left = `${Math.max(margin, rect.left - overflowX)}px`
  if (overflowY > 0) element.style.top = `${Math.max(margin, rect.top - overflowY)}px`
}

/**
 * The desktop's own right-click menu: how the icons are arranged.
 *
 * Deliberately only offered on the wallpaper. Right-clicking inside a window
 * has to keep the browser's own menu, or the terminal loses paste and every
 * app loses "open in new tab".
 */
const DesktopMenu = ({ x, y, current, onClose }) => {
  const ref = useRef(null)
  const firstRef = useRef(null)

  useEffect(() => {
    if (ref.current) clampToDesktop(ref.current)
    firstRef.current?.focus()
  }, [])

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === "Escape") onClose()
    }
    // A second right-click elsewhere should move the menu, not stack another.
    const onPointerDown = e => {
      if (!ref.current?.contains(e.target)) onClose()
    }

    document.addEventListener("keydown", onKeyDown)
    document.addEventListener("pointerdown", onPointerDown)
    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.removeEventListener("pointerdown", onPointerDown)
    }
  }, [onClose])

  return (
    <div
      ref={ref}
      className="desk-menu"
      style={{ left: x, top: y }}
      role="menu"
      aria-label="Desktop"
    >
      <p className="desk-menu-heading">Sort icons by</p>
      {ORDERS.map((order, i) => (
        <button
          key={order.key}
          ref={i === 0 ? firstRef : null}
          type="button"
          role="menuitemradio"
          aria-checked={current === order.key}
          className={`desk-menu-item ${current === order.key ? "is-current" : ""}`}
          onClick={() => {
            setOrder(order.key)
            onClose()
          }}
        >
          <span className="desk-menu-tick" aria-hidden="true">
            {current === order.key ? "•" : ""}
          </span>
          {order.label}
        </button>
      ))}
    </div>
  )
}

export default DesktopMenu
